import type { CartItemOutputDto, CartItemResponseDto, CartOutputDto } from "@sticker-giant/sg-frontend-common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CART_ACTIVE_FILTER, CART_QUERY_KEY, commitCart } from "@/queries/useCart";
import { useServicesStore } from "@/stores/useServicesStore";

export const duplicateCartItemMutationKey = ["duplicateCartItem"] as const;

export function useDuplicateCartItem(itemId: string) {
  const cartClient = useServicesStore(state => state.cartClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: duplicateCartItemMutationKey,
    mutationFn: async () => {
      if (!cartClient) throw new Error("Cart client is not available.");
      return await cartClient.postDuplicateItem(itemId);
    },
    onMutate: async (item: CartItemOutputDto) => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY });

      const snapshots = queryClient.getQueriesData<CartOutputDto | null>(CART_ACTIVE_FILTER);

      queryClient.setQueriesData<CartOutputDto | null>(
        CART_ACTIVE_FILTER,
        (current: CartOutputDto | null | undefined) => {
          if (!current) return current;

          const newItem = {
            ...item,
            id: `${item.id}-duplicate-${Date.now()}`,
            position: item.position + 1,
            isDuplicate: true,
          };

          const updatedItems = current.items.map(cartItem =>
            cartItem.position > item.position ? { ...cartItem, position: cartItem.position + 1 } : cartItem,
          );
          updatedItems.push(newItem);
          updatedItems.sort((itemA, itemB) => itemA.position - itemB.position);

          return { ...current, items: updatedItems };
        },
      );

      return { snapshots };
    },
    onSuccess: (response?: CartItemResponseDto) => {
      if (!response) return;
      commitCart(queryClient, response.cart);
    },
    onError: (_error, _variables, context) => {
      context?.snapshots.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
  });
}
